import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, useMemo } from 'react'
import { Activity, Database, CheckCircle, AlertCircle, Clock, TrendingUp, DollarSign, AlertTriangle } from 'lucide-react'
import api from '../lib/api'
import { AGENT_WORKER_HOST, isDev } from '../lib/config'

// Types for metrics data
interface MetricsSummary {
  window: string
  latency: { avg: number; p50: number; p99: number }
  errors: Record<string, number>
  cost: { tokens: number; usd: string }
  qps: number
  timestamp: string
}

interface TimeSeriesPoint {
  timestamp: string
  value: number
  count: number
  group?: string
}

interface AlertRule {
  id: string
  name: string
  metric: string
  threshold: number
  condition: 'gt' | 'gte' | 'lt' | 'lte' | 'eq'
  window: string
  notifyChannels: ('email' | 'webhook' | 'slack')[]
  enabled: boolean
  createdAt: string
}

export default function Monitor() {
  const queryClient = useQueryClient()
  const [timeRange, setTimeRange] = useState<'1h' | '6h' | '24h' | '7d'>('1h')
  const [selectedMetric, setSelectedMetric] = useState('http_request_duration_ms')
  const [sseConnected, setSseConnected] = useState(false)

  // Real-time metrics via SSE
  useEffect(() => {
    let eventSource: EventSource | null = null
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
    
    const connect = () => {
      // Close existing connection before creating new one
      if (eventSource) {
        eventSource.close()
        eventSource = null
      }
      
      try {
        // Use worker host in production, relative path in dev (proxied by Vite)
        const baseUrl = isDev ? '' : `https://${AGENT_WORKER_HOST}`
        eventSource = new EventSource(`${baseUrl}/api/stream/metrics`)
        
        eventSource.onopen = () => setSseConnected(true)
        eventSource.onerror = () => {
          setSseConnected(false)
          // Reconnect after 5 seconds
          reconnectTimeout = setTimeout(connect, 5000)
        }
        
        eventSource.addEventListener('metrics_update', (e) => {
          const data = JSON.parse(e.data)
          // Invalidate summary query to trigger refresh
          queryClient.invalidateQueries({ queryKey: ['metrics', 'summary'] })
        })
      } catch {
        // Fallback to polling
        setSseConnected(false)
      }
    }
    
    connect()
    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      eventSource?.close()
    }
  }, [queryClient])

  // Query hooks
  const { data: healthData } = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get('/monitor/health').then(res => res.data),
    refetchInterval: 30000
  })

  const { data: metricsSummary } = useQuery({
    queryKey: ['metrics', 'summary', timeRange],
    queryFn: () => api.get(`/api/metrics/summary?window=${timeRange}`).then(res => res.data),
    refetchInterval: sseConnected ? false : 30000,
  })

  const { data: timeSeriesData } = useQuery({
    queryKey: ['metrics', 'timeseries', selectedMetric, timeRange],
    queryFn: () => api.get(`/api/metrics/timeseries?metric=${selectedMetric}&window=${timeRange}`).then(res => res.data),
    enabled: !!selectedMetric,
    refetchInterval: 60000,
  })

  const { data: alertRules } = useQuery({
    queryKey: ['metrics', 'alerts'],
    queryFn: () => api.get('/api/metrics/alerts').then(res => res.data),
    refetchInterval: 60000,
  })

  // Computed metrics for display
  const healthStatus = useMemo(() => {
    const checks = healthData?.data?.checks || {}
    const allOk = Object.values(checks).every((c: any) => c?.ok)
    return {
      status: allOk ? 'healthy' as const : 'degraded' as const,
      checks,
    }
  }, [healthData])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-orange-500" />
          <h2 className="text-2xl font-bold">系统监控</h2>
          <span className={`px-2 py-1 rounded text-xs ${
            sseConnected ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'
          }`}>
            {sseConnected ? '实时连接' : '轮询模式'}
          </span>
        </div>
        
        {/* Time range selector */}
        <div className="flex gap-2">
          {(['1h', '6h', '24h', '7d'] as const).map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 rounded text-sm ${
                timeRange === range 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Health Status Card */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-2">系统健康状态</h3>
            <div className="flex items-center gap-2">
              {healthStatus.status === 'healthy' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-500 font-medium">系统正常</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <span className="text-red-500 font-medium">系统异常</span>
                </>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-400">最后检查</p>
            <p className="text-sm">
              {healthData?.data?.timestamp
                ? new Date(healthData.data.timestamp).toLocaleString()
                : '加载中...'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <ServiceStatus label="D1 数据库" status={healthStatus.checks.database} />
          <ServiceStatus label="KV 存储" status={healthStatus.checks.kv} />
          <ServiceStatus label="Analytics Engine" status={healthStatus.checks.analytics} />
          <ServiceStatus label="AI 服务" status={healthStatus.checks.ai} />
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={TrendingUp}
          label="P99 延迟"
          value={`${metricsSummary?.data?.latency?.p99 || 0}ms`}
          trend={metricsSummary?.data?.latency?.p99 < 500 ? 'good' : 'warning'}
        />
        <MetricCard
          icon={Activity}
          label="QPS"
          value={metricsSummary?.data?.qps?.toString() || '0'}
          trend="neutral"
        />
        <MetricCard
          icon={AlertTriangle}
          label="错误率"
          value={`${Object.values(metricsSummary?.data?.errors || {}).reduce((a,b) => a+b, 0)}`}
          trend={Object.values(metricsSummary?.data?.errors || {}).reduce((a,b) => a+b, 0) === 0 ? 'good' : 'warning'}
        />
        <MetricCard
          icon={DollarSign}
          label="估算成本"
          value={`$${metricsSummary?.data?.cost?.usd || '0'}`}
          subValue={`${(metricsSummary?.data?.cost?.tokens || 0).toLocaleString()} tokens`}
          trend="neutral"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Latency Chart */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">请求延迟分布</h3>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="bg-gray-700 rounded px-2 py-1 text-sm"
            >
              <option value="http_request_duration_ms">HTTP 请求</option>
              <option value="chat_latency_ms">AI 对话</option>
              <option value="workflow_step_duration">工作流步骤</option>
            </select>
          </div>
          <SimpleChart 
            data={timeSeriesData?.data?.points || []} 
            color="#f97316"
            height={200}
          />
        </div>

        {/* Error Breakdown */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold mb-4">错误类型分布</h3>
          <div className="space-y-3">
            {Object.entries(metricsSummary?.data?.errors || {}).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-sm text-gray-300">{type}</span>
                <span className="font-medium text-red-400">{count}</span>
              </div>
            ))}
            {Object.keys(metricsSummary?.data?.errors || {}).length === 0 && (
              <p className="text-gray-500 text-sm">无错误记录</p>
            )}
          </div>
        </div>
      </div>

      {/* Alert Rules Section */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">告警规则</h3>
          <button className="px-3 py-1 bg-orange-500 hover:bg-orange-600 rounded text-sm transition-colors">
            + 新建规则
          </button>
        </div>
        <div className="space-y-2">
          {alertRules?.data?.map((rule: AlertRule) => (
            <div key={rule.id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${rule.enabled ? 'bg-green-500' : 'bg-gray-500'}`} />
                <div>
                  <p className="text-sm font-medium">{rule.name}</p>
                  <p className="text-xs text-gray-400">
                    {rule.metric} {rule.condition} {rule.threshold} ({rule.window})
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  {rule.notifyChannels.join(', ')}
                </span>
                <button className="text-gray-400 hover:text-white text-sm">编辑</button>
              </div>
            </div>
          ))}
          {(!alertRules?.data || alertRules.data.length === 0) && (
            <p className="text-gray-500 text-sm">暂无告警规则</p>
          )}
        </div>
      </div>
    </div>
  )
}

// Helper Components
function ServiceStatus({ label, status }: { label: string; status?: { ok: boolean; latency_ms?: number } }) {
  if (!status) {
    return (
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-gray-500" />
        <span className="text-sm">{label}</span>
        <span className="ml-auto text-sm text-gray-500">未知</span>
      </div>
    )
  }
  
  return (
    <div className="flex items-center gap-2">
      {status.ok ? (
        <CheckCircle className="w-4 h-4 text-green-500" />
      ) : (
        <AlertCircle className="w-4 h-4 text-red-500" />
      )}
      <span className="text-sm">{label}</span>
      <span className={`ml-auto text-sm ${status.ok ? 'text-green-500' : 'text-red-500'}`}>
        {status.ok ? `${status.latency_ms}ms` : '异常'}
      </span>
    </div>
  )
}

function MetricCard({ icon: Icon, label, value, subValue, trend }: {
  icon: any
  label: string
  value: string
  subValue?: string
  trend: 'good' | 'warning' | 'neutral'
}) {
  const trendColors = {
    good: 'text-green-500',
    warning: 'text-yellow-500',
    neutral: 'text-gray-400',
  }
  
  return (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${trendColors[trend]}`} />
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
    </div>
  )
}

function SimpleChart({ data, color, height }: { 
  data: TimeSeriesPoint[]
  color: string
  height: number
}) {
  if (!data || data.length === 0) {
    return <div className="h-48 flex items-center justify-center text-gray-500">暂无数据</div>
  }
  
  const maxValue = Math.max(...data.map(d => d.value))
  const minValue = Math.min(...data.map(d => d.value))
  const range = maxValue - minValue || 1
  
  return (
    <div className="relative" style={{ height }}>
      <svg width="100%" height={height} className="overflow-visible">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(p => (
          <line
            key={p}
            x1="0"
            y1={height * (1 - p)}
            x2="100%"
            y2={height * (1 - p)}
            stroke="#374151"
            strokeDasharray="4"
          />
        ))}
        
        {/* Data line */}
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          points={data.map((d, i) => {
            const x = (i / (data.length - 1)) * 100 + '%'
            const y = height - ((d.value - minValue) / range) * height
            return `${x},${y}`
          }).join(' ')}
        />
        
        {/* Data points */}
        {data.map((d, i) => {
          const x = (i / (data.length - 1)) * 100 + '%'
          const y = height - ((d.value - minValue) / range) * height
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="3"
              fill={color}
              className="hover:r-4 transition-all"
            />
          )
        })}
      </svg>
      
      {/* X-axis labels */}
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        <span>{data[0]?.timestamp?.slice(11, 16)}</span>
        <span>{data[data.length - 1]?.timestamp?.slice(11, 16)}</span>
      </div>
    </div>
  )
}
